import React, { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import block from 'bem-cn'

import { useAppTranslation } from 'shared/hooks/useAppTranslation'
import { useClickOutside } from 'shared/hooks/useClickOutside'

import { Portal } from 'components/UIKit/Portal'

import { ReactComponent as Arrow } from './img/arrow.svg'

import './Select.scss'

export type SelectOption = {
  value: string
  suffix?: React.ReactNode
  prefix?: React.ReactNode
  label?: string
}

export type DropdownPosition = 'top' | 'bottom'

export type SelectProps = {
  value?: SelectOption
  options?: SelectOption[]
  placeholder?: string
  className?: string
  dropdownClassName?: string
  caption?: React.ReactNode

  disabled?: boolean
  dropdownPosition?: DropdownPosition
  onChange?: (option: SelectOption, value: string) => void
}

const b = block('select')

export const Select: FC<SelectProps> = ({
  options = [],
  value,
  className,
  dropdownClassName,
  caption,
  disabled,
  onChange,
  dropdownPosition,
  placeholder,
}) => {
  const { t } = useAppTranslation()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [isOpened, setIsOpened] = useState(false)
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    if (dropdownPosition === 'top') {
      setDropdownStyle({
        position: 'fixed',
        left: rect.left,
        top: rect.top - 4,
        width: rect.width,
        transform: 'translateY(-100%)',
      })
    } else {
      setDropdownStyle({
        position: 'fixed',
        left: rect.left,
        top: rect.bottom + 4,
        width: rect.width,
      })
    }
  }, [dropdownPosition])

  useLayoutEffect(() => {
    if (isOpened) updatePosition()
  }, [isOpened, updatePosition])

  useEffect(() => {
    if (!isOpened) {
      return undefined
    }

    const handleUpdate = () => {
      updatePosition()
    }

    window.addEventListener('scroll', handleUpdate, true)
    window.addEventListener('resize', handleUpdate)
    window.visualViewport?.addEventListener('resize', handleUpdate)

    return () => {
      window.removeEventListener('scroll', handleUpdate, true)
      window.removeEventListener('resize', handleUpdate)
      window.visualViewport?.removeEventListener('resize', handleUpdate)
    }
  }, [isOpened, updatePosition])

  useClickOutside(dropdownRef, () => {
    setIsOpened(false)
  }, triggerRef)

  const handleSelect = (option: SelectOption) => {
    onChange?.(option, option.value)
    setIsOpened(false)
  }

  const handleTriggerClick = () => {
    if (!disabled) setIsOpened(prev => !prev)
  }

  return (
    <div className={b({ isOpened }).mix(className)}>
      {caption && <div className={b('caption')}>{caption}</div>}
      <button
        ref={triggerRef}
        type="button"
        className={b('trigger', { isOpened })}
        onClick={handleTriggerClick}
        disabled={disabled}>
        {value ? (
          <span className={b('selected-value')}>{value.label || value.value}</span>
        ) : (
          <span className={b('placeholder')}>{placeholder ? t(placeholder) : null}</span>
        )}
        <Arrow className={b('arrow', { isOpened })} />
      </button>

      {isOpened && (
        <Portal>
          <div
            ref={dropdownRef}
            className={b('dropdown', { position: dropdownPosition }).mix(dropdownClassName)}
            style={dropdownStyle}>
            <div className={b('options')}>
              {options.length === 0 ? (
                <div className={b('empty')}>{t('common.noOptions')}</div>
              ) : (
                options.map(option => (
                  <div
                    key={option.value}
                    className={b('option', { selected: value?.value === option.value })}
                    onClick={() => handleSelect(option)}>
                    {option.label || option.value}
                  </div>
                ))
              )}
            </div>
          </div>
        </Portal>
      )}
    </div>
  )
}
